import Konva from 'konva'
import getShapes from './shapes-mutator.es6'

class Anchor extends Konva.Group {
  constructor (initAttrs, renAttrs, eventHandlers) {
    super(initAttrs)
    // Properties
    this.idx = undefined // Index of the anchor (0-based)
    // cannot be named 'index' as it probably collides with Konva
    Object.assign(this, renAttrs)
    // Event handlers (must bind this from _eventsParent)
    this.handleAnchorDragmove = eventHandlers.handleAnchorDragmove.bind(this._eventsParent)
    this.handleAnchorDragend = eventHandlers.handleAnchorDragend.bind(this._eventsParent)

    // Update shapes
    this._updateAllShapes()

    // Bind own events
    // this.on('dragstart', () => {
    //   // this.destroyRenRotator()
    // })

    this.on('dragmove', () => {
      this.handleAnchorDragmove(this.point, this.idx)
    })

    this.on('dragend', () => {
      this.handleAnchorDragend(true)
    })
  }

  // GETTERS AND SETTERS
  get point () {
    return [this.attrs.x, this.attrs.y]
  }

  // METHODS
  _updateAllShapes () {
    // Destroy existing shapes
    this.destroyChildren()

    // Create new shapes
    const shapes = getShapes.anchor(this._scale)
    shapes.forEach(shape => this.add(shape))
  }
}

class Wrap extends Konva.Group {
  constructor (initAttrs, renAttrs, eventHandlers) {
    super(initAttrs)
    // Properties
    this._renElementShapes = []
    this._pattern = undefined
    this._elementTypeName = undefined
    this._hover = undefined
    Object.assign(this, renAttrs)

    // Update shapes
    this._updateAllShapes()
  }

  // METHODS
  _updateAllShapes () {
    // Destroy existing shapes
    this.destroyChildren()

    // Create new shapes
    let shapes = []
    if (this._pattern === 'node' || this._elementTypeName === 'tc_vector') {
      shapes = getShapes.wrap(this._renElementShapes, this._hover, this._scale)
    } else {
      const renLine = [...this._renElementShapes].find(shape => shape.attrs.name === 'line')
      shapes = renLine ? getShapes.vectorWrap(renLine, this._hover) : []
    }
    shapes.forEach(shape => this.add(shape))
  }
}

class Rotator extends Konva.Group {
  constructor (initAttrs, renAttrs, eventHandlers) {
    super(initAttrs)
    // Properties
    Object.assign(this, renAttrs)
    // Event handlers (must bind this from _eventsParent)
    this.handleRotatorDragmove = eventHandlers.handleRotatorDragmove.bind(this._eventsParent)
    this.handleRotatorDragend = eventHandlers.handleRotatorDragend.bind(this._eventsParent)

    // Update shapes (incl. vector)
    this._updateAllShapes()

    // Bind events
    this.on('dragmove', () => {
      this.handleRotatorDragmove(this.point)
    })

    this.on('dragend', () => {
      this.handleRotatorDragend()
    })
  }

  // GETTERS AND SETTERS
  get point () {
    return [this.attrs.x, this.attrs.y]
  }

  // METHODS
  _updateAllShapes () {
    // Destroy existing shapes
    this.destroyChildren()

    // Create new shapes
    const shapes = getShapes.rotator(this._scale)
    shapes.forEach(shape => this.add(shape))
  }
}

class RotateVector extends Konva.Group {
  constructor (initAttrs, renAttrs) {
    super(initAttrs)
    // Properties
    Object.assign(this, renAttrs)

    // Update shapes
    this._updateAllShapes()
  }

  // METHODS
  _updateAllShapes () {
    // Destroy existing shapes
    this.destroyChildren()

    // Create new shapes
    const shapes = getShapes.rotateVector(this._scale)
    shapes.forEach(shape => this.add(shape))
  }
}

class SelRect extends Konva.Group {
  constructor (initAttrs, renAttrs, eventHandlers) {
    super(initAttrs)
    // Properties
    this.renStage = undefined
    Object.assign(this, renAttrs)

    // Update shapes
    this._updateAllShapes()

    // Bind events
    this.renStage.on('mousemove.selRect', () => {
      this.resizeRectangle(this.renStage.stagePosition)
    })
  }

  destroy () {
    // Unbind stage event
    this.renStage.off('mousemove.selRect')

    // Destroy
    super.destroy()
  }

  _updateAllShapes () {
    // Destroy existing shapes
    this.destroyChildren()

    // Create new shapes
    const shapes = getShapes.selRect()
    shapes.forEach(shape => this.add(shape))
  }

  resizeRectangle (pos) { // point is the opposite corner of the rectangle
    this.children[0].width(pos.x - this.attrs.x)
    this.children[0].height(pos.y - this.attrs.y)
    // Draw the layer
    this.layerBatchDraw()
  }

  endSelection (discardSelection = false) {
    const layer = this.parent

    if (!discardSelection) {
      const selectedElementsIds = this._getRenElementsInSelRect()
      this.renStage.handleElementsSelected(selectedElementsIds)
    }

    this.destroy()
    layer.draw()
  }

  _getRenElementsInSelRect () { // rectPoints = [x0, y0, x1, y1]
    const x0 = this.attrs.x
    const y0 = this.attrs.y
    const x1 = this.attrs.x + this.children[0].attrs.width
    const y1 = this.attrs.y + this.children[0].attrs.height

    const selectedElementsIds = this.renStage.renElements
      .filter(renElement => {
        let allPointsInside = true

        if (renElement.pairedPoints.length === 0) {
          // Element has no points
          allPointsInside = false
        }

        renElement.pairedPoints.forEach(point => {
          const x = point[0]
          const y = point[1]
          if (Math.max(x, x0, x1) === x ||
            Math.min(x, x0, x1) === x ||
            Math.max(y, y0, y1) === y ||
            Math.min(y, y0, y1) === y) {
            // Point is not present inside the selRect
            allPointsInside = false
          }
        })

        // All points are present inside the selRect
        return allPointsInside
      })
      .map(renElement => renElement._element.id)

    return selectedElementsIds
  }

  layerBatchDraw () {
    this.parent.batchDraw()
  }
}

class Grid extends Konva.Group {
  constructor ({ span, extent, detailed, zoomLevel }) {
    super({
      x: 0,
      y: 0,
      rotation: 0,
      draggable: false
    })
    Object.assign(this, { span, extent, detailed, zoomLevel })

    // Update shapes
    this._updateAllShapes()
  }

  // METHODS
  _updateAllShapes () {
    // Destroy existing shapes
    this.destroyChildren()

    // Create new shapes
    const { span, extent, detailed, zoomLevel } = this
    const shapes = getShapes.grid({ span, extent, detailed, zoomLevel })
    shapes.forEach(shape => this.add(shape))
  }
}

class Bottom extends Konva.Group {
  constructor (initAttrs, renAttrs, eventHandlers) {
    super(Object.assign({
      x: 0,
      y: 0,
      rotation: 0,
      draggable: false
    }, initAttrs))
    // Properties
    this.gridExtent = undefined
    this.renStage = undefined
    Object.assign(this, renAttrs)
    // Event handlers (must bind this from _eventsParent)
    this.handleClick = eventHandlers.handleClick.bind(this._eventsParent)

    // Update shapes
    this._updateAllShapes()

    // Bind events
    this.on('click', (event) => {
      console.log('EVT[ren-mutator][bottom]:click')
      this.handleClick(event)
    })
  }

  // METHODS
  _updateAllShapes () {
    // Destroy existing shapes
    this.destroyChildren()

    // Create new shapes
    const shapes = getShapes.bottom(this.gridExtent)
    shapes.forEach(shape => this.add(shape))
  }
}

export default { Anchor, Wrap, Rotator, RotateVector, SelRect, Grid, Bottom }
